<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive } from 'vue';
import { useI18n } from 'vue-i18n';

import axios from 'axios';

import { useOtcAPIService } from '@/composables/services/useOtcAPIService';
import { useTokens } from '@/composables/useTokens';
import { isAddress } from '@/helpers/utils';
import { captureSentryException } from '@/logs/sentry';
import { TOKEN_SEARCH_TERM_DEBOUNCE } from '@/references/constants';
import type { Network } from '@/references/network';
import type { TokenWithEurPrice } from '@/references/tokens';

import ListSkeleton from '@/components/ListSkeleton.vue';
import TokenInfoWithEur from '@/components/TokenInfoWithEur.vue';
import TokenSelectorHeader from '@/components/TokenSelectorHeader.vue';
import UiNotFound from '@/components/ui/UiNotFound.vue';
import UiText from '@/components/ui/UiText.vue';

type TokenSelectorGroup = {
  label: string;
  tokens: ListItem<TokenWithEurPrice>[];
};

type ListItem<T extends Record<string, unknown>> = T & { key: string };

interface Props {
  network: Network;
  networks: Network[];
  forceNetworks?: Network[];
  isNetworkSelectorReadonly?: boolean;
  localStorageTipKey?: string;
  sticky?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isNetworkSelectorReadonly: false,
  localStorageTipKey: undefined,
  forceNetworks: undefined,
  sticky: true
});

const emit = defineEmits<{
  (event: 'clear'): void;
  (event: 'select', value: TokenWithEurPrice): void;
  (event: 'update:network', value: Network): void;
}>();

const state = reactive<{
  searchValue: string;
  debouncedSearchValue: string;
  searchValueDebounce: ReturnType<typeof setTimeout> | undefined;

  foundTokens: TokenWithEurPrice[];

  isSearching: boolean;
  isSearched: boolean;
}>({
  searchValue: '',
  debouncedSearchValue: '',
  searchValueDebounce: undefined,

  isSearching: false,
  isSearched: false,

  foundTokens: []
});

let searchAbortController: AbortController = new AbortController();

const { t } = useI18n();
const { assets: allPopularTokens, loaded: isTokensLoaded, searchTokens } = useTokens();

const networks = computed((): Network[] => {
  if (props.forceNetworks) {
    return props.forceNetworks;
  }
  const service = useOtcAPIService();

  return service.getAvailableNetworks();
});

const isLoading = computed((): boolean => {
  if (!state.searchValue.length) {
    return false;
  }

  return state.isSearching || !state.isSearched;
});

const isSkeletonVisible = computed((): boolean => !isTokensLoaded.value);

const foundTokens = computed((): ListItem<TokenWithEurPrice>[] => {
  return state.foundTokens.map((t) => {
    (t as ListItem<TokenWithEurPrice>).key = `${t.network}${t.address}`;
    return t as ListItem<TokenWithEurPrice>;
  });
});

const popularTokens = computed((): ListItem<TokenWithEurPrice>[] => {
  return allPopularTokens.value
    .filter((t) => t.network === props.network)
    .map((t) => {
      (t as ListItem<TokenWithEurPrice>).key = `${t.network}${t.address}`;
      return t as ListItem<TokenWithEurPrice>;
    });
});

const groups = computed((): TokenSelectorGroup[] => {
  if (state.searchValue === '' && popularTokens.value.length !== 0) {
    return [
      {
        label: t('forms.tokenSelector.popular'),
        tokens: popularTokens.value
      }
    ];
  }

  if (foundTokens.value.length !== 0) {
    return [
      {
        label: t('forms.tokenSelector.found'),
        tokens: foundTokens.value
      }
    ];
  }

  return [];
});

function selectToken(token: TokenWithEurPrice): void {
  emit('select', token);
}

function updateSearchValue(value: string): void {
  const searchTerm = value.trim().toLowerCase();
  const networkFilter = props.network;
  state.searchValue = searchTerm;
  state.isSearched = false;

  searchAbortController.abort();
  clearTimeout(state.searchValueDebounce);
  state.searchValueDebounce = undefined;

  const abortController = new AbortController();
  searchAbortController = abortController;

  if (value === '') {
    state.debouncedSearchValue = searchTerm;
    state.isSearched = true;
    return;
  }

  if (isAddress(searchTerm)) {
    // execute search right away, do not wait for further inputs
    updateFoundTokensBySearchValue(searchTerm, networkFilter, abortController.signal);
    return;
  }

  state.searchValueDebounce = setTimeout(() => {
    updateFoundTokensBySearchValue(searchTerm, networkFilter, abortController.signal);
  }, TOKEN_SEARCH_TERM_DEBOUNCE);
}

async function updateFoundTokensBySearchValue(
  searchTerm: string,
  network: Network,
  abortSignal: AbortSignal
): Promise<void> {
  try {
    state.isSearching = true;

    state.foundTokens = await searchTokens(searchTerm, network, abortSignal);
  } catch (error) {
    if (axios.isCancel(error)) {
      return;
    }

    captureSentryException(
      new Error('Failed to update tokens by search term', {
        cause: error
      })
    );
  } finally {
    state.isSearching = false;
    state.isSearched = true;
  }
}

function updateNetworkFilter(network: Network | null): void {
  if (network) {
    searchAbortController.abort();
    clearTimeout(state.searchValueDebounce);
    state.searchValueDebounce = undefined;

    state.foundTokens = [];

    emit('update:network', network);
  }
}

onMounted(() => {
  updateNetworkFilter(props.network);
});

onBeforeUnmount(() => {
  searchAbortController.abort();
});
</script>

<template>
  <div class="token-selector" :class="{ 'token-selector_sticky': props.sticky }">
    <TokenSelectorHeader
      class="token-selector__header"
      :placeholder="t('forms.searchAnyToken')"
      :model-value="state.searchValue"
      :network="props.network"
      :networks="networks"
      :use-unset="false"
      :local-storage-tip-key="props.localStorageTipKey"
      :is-network-selector-readonly="props.isNetworkSelectorReadonly"
      :loading="isLoading"
      @update:model-value="updateSearchValue"
      @update:network="updateNetworkFilter"
    />
    <template v-if="!isLoading">
      <ListSkeleton v-if="isSkeletonVisible" />
      <ul v-else-if="groups.length" class="token-selector__group-list">
        <li v-for="group in groups" :key="group.label" class="token-selector__group-item">
          <UiText class="token-selector__label" muted size="sm" weight="medium" tag="h3">
            {{ group.label }}
          </UiText>
          <ul class="token-selector__token-list">
            <li v-for="token in group.tokens" :key="token.key" class="token-selector__token-item">
              <TokenInfoWithEur :token="token" @click="selectToken(token)" />
            </li>
          </ul>
        </li>
      </ul>
      <UiNotFound v-else />
    </template>
  </div>
</template>

<style scoped>
.token-selector__header {
  margin: 0 0 40px;
}
@media (max-width: 919px) {
  .token-selector__header {
    margin: 0 0 24px;
  }
}

.token-selector_sticky .token-selector__header {
  position: sticky;
  top: 96px;
  z-index: var(--z-index-header);
}

@media (max-width: 919px) {
  .token-selector_sticky .token-selector__header {
    top: 80px;
  }
}

.token-selector__group-list {
  margin: 0;
  padding: 0;
  list-style-type: none;
}

.token-selector__group-item {
  padding: 16px 16px 4px;
  background-color: var(--color-main-underlay);
  border-radius: 12px;
}

.token-selector__group-item:not(:last-child) {
  margin: 0 0 40px;
}

.token-selector__label {
  margin: 0 0 4px;
}

.token-selector__token-list {
  margin: 0;
  padding: 0;
  list-style-type: none;
}
</style>
